// DesignStudio.js — Professional Canva-style Design Studio
// Always-open left sidebar · 5 tabs: BG | Layout | Text | Shapes | Brand
// 12 predefined CSS backgrounds · fabric.Textbox word-wrap · AI text · Full alignment

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { businessAPI } from '../../../services/businessAPI';
import { removeBackground as rmbgAI } from '@imgly/background-removal';

// ─── Aspect Ratios ─────────────────────────────────────────────────────────────
const ASPECT_RATIOS = {
  square:    { label: '1:1',  w: 1,  h: 1,  hint: 'Instagram · Facebook' },
  portrait:  { label: '4:5',  w: 4,  h: 5,  hint: 'Instagram Portrait' },
  story:     { label: '9:16', w: 9,  h: 16, hint: 'Stories · TikTok' },
  landscape: { label: '16:9', w: 16, h: 9,  hint: 'LinkedIn · YouTube' },
};

// ─── 12 Predefined Background Presets (canvas-rendered, no AI products) ─────────
const BG_PRESETS = [
  {
    id: 'purpleAmberWave', name: 'Purple Wave', colors: ['#5B21B6', '#F59E0B'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, h * 0.8);
      g.addColorStop(0, '#4C1D95'); g.addColorStop(1, '#7C2D8E');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.65);
      ctx.bezierCurveTo(w * 0.2, h * 0.52, w * 0.55, h * 0.78, w * 0.78, h * 0.60);
      ctx.bezierCurveTo(w * 0.88, h * 0.52, w * 0.95, h * 0.62, w, h * 0.58);
      ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
      const ag = ctx.createLinearGradient(0, h * 0.58, 0, h);
      ag.addColorStop(0, '#F59E0B'); ag.addColorStop(1, '#B45309');
      ctx.fillStyle = ag; ctx.fill();
      return c.toDataURL();
    },
  },
  {
    id: 'blueOcean', name: 'Blue Ocean', colors: ['#0EA5E9', '#0C4A6E'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#0EA5E9'); g.addColorStop(0.5, '#0369A1'); g.addColorStop(1, '#0C4A6E');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.75);
      ctx.bezierCurveTo(w * 0.3, h * 0.63, w * 0.7, h * 0.85, w, h * 0.70);
      ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fill();
      return c.toDataURL();
    },
  },
  {
    id: 'sunsetPink', name: 'Sunset', colors: ['#BE185D', '#F97316'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#BE185D'); g.addColorStop(0.5, '#EC4899'); g.addColorStop(1, '#F97316');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      const rg = ctx.createRadialGradient(w * 0.5, h * 0.55, 0, w * 0.5, h * 0.55, w * 0.4);
      rg.addColorStop(0, 'rgba(255,220,100,0.45)'); rg.addColorStop(1, 'rgba(255,220,100,0)');
      ctx.fillStyle = rg; ctx.fillRect(0, 0, w, h);
      return c.toDataURL();
    },
  },
  {
    id: 'emeraldForest', name: 'Emerald', colors: ['#064E3B', '#10B981'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#022C22'); g.addColorStop(0.5, '#064E3B'); g.addColorStop(1, '#065F46');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.70);
      ctx.bezierCurveTo(w * 0.25, h * 0.60, w * 0.6, h * 0.80, w, h * 0.65);
      ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
      ctx.globalAlpha = 0.8; ctx.fillStyle = '#10B981'; ctx.fill(); ctx.globalAlpha = 1;
      return c.toDataURL();
    },
  },
  {
    id: 'navyPro', name: 'Navy Pro', colors: ['#0F2136', '#3B82F6'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#0F2136'; ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.60); ctx.lineTo(w * 0.62, h * 0.28);
      ctx.lineTo(w, h * 0.38); ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
      ctx.fillStyle = 'rgba(59,130,246,0.22)'; ctx.fill();
      const rg = ctx.createRadialGradient(w * 0.6, h * 0.3, 0, w * 0.6, h * 0.3, w * 0.45);
      rg.addColorStop(0, 'rgba(59,130,246,0.25)'); rg.addColorStop(1, 'rgba(59,130,246,0)');
      ctx.fillStyle = rg; ctx.fillRect(0, 0, w, h);
      return c.toDataURL();
    },
  },
  {
    id: 'roseGold', name: 'Rose Gold', colors: ['#9D174D', '#FDF2F8'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#9D174D'); g.addColorStop(0.55, '#C2185B'); g.addColorStop(1, '#F8BBD0');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      [[0.15,0.2],[0.8,0.15],[0.45,0.65],[0.7,0.8],[0.25,0.85]].forEach(([sx,sy]) => {
        const rg = ctx.createRadialGradient(sx*w,sy*h,0,sx*w,sy*h,w*0.18);
        rg.addColorStop(0,'rgba(255,255,255,0.18)'); rg.addColorStop(1,'rgba(255,255,255,0)');
        ctx.fillStyle = rg; ctx.fillRect(0,0,w,h);
      });
      return c.toDataURL();
    },
  },
  {
    id: 'midnightPurple', name: 'Midnight', colors: ['#0F0720', '#A855F7'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#0F0720'; ctx.fillRect(0, 0, w, h);
      const rg1 = ctx.createRadialGradient(w*0.7,h*0.25,0,w*0.7,h*0.25,w*0.55);
      rg1.addColorStop(0,'rgba(168,85,247,0.55)'); rg1.addColorStop(0.5,'rgba(88,28,220,0.28)'); rg1.addColorStop(1,'rgba(88,28,220,0)');
      ctx.fillStyle = rg1; ctx.fillRect(0,0,w,h);
      const rg2 = ctx.createRadialGradient(w*0.2,h*0.8,0,w*0.2,h*0.8,w*0.4);
      rg2.addColorStop(0,'rgba(59,130,246,0.3)'); rg2.addColorStop(1,'rgba(59,130,246,0)');
      ctx.fillStyle = rg2; ctx.fillRect(0,0,w,h);
      return c.toDataURL();
    },
  },
  {
    id: 'goldenHour', name: 'Golden Hour', colors: ['#B45309', '#FDE68A'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0,'#92400E'); g.addColorStop(0.4,'#D97706'); g.addColorStop(0.85,'#F59E0B'); g.addColorStop(1,'#FDE68A');
      ctx.fillStyle = g; ctx.fillRect(0,0,w,h);
      const rg = ctx.createRadialGradient(w*0.5,h*0.48,0,w*0.5,h*0.48,w*0.42);
      rg.addColorStop(0,'rgba(255,255,200,0.4)'); rg.addColorStop(1,'rgba(255,255,200,0)');
      ctx.fillStyle = rg; ctx.fillRect(0,0,w,h);
      return c.toDataURL();
    },
  },
  {
    id: 'tealFresh', name: 'Teal Fresh', colors: ['#0F766E', '#14B8A6'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0,'#0F766E'); g.addColorStop(1,'#14B8A6');
      ctx.fillStyle = g; ctx.fillRect(0,0,w,h);
      ctx.beginPath();
      ctx.moveTo(0, h*0.72);
      ctx.bezierCurveTo(w*0.3, h*0.62, w*0.7, h*0.80, w, h*0.68);
      ctx.lineTo(w,h); ctx.lineTo(0,h); ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fill();
      return c.toDataURL();
    },
  },
  {
    id: 'berryBold', name: 'Berry Bold', colors: ['#4C1D95', '#DB2777'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(w, 0, 0, h);
      g.addColorStop(0,'#4C1D95'); g.addColorStop(0.55,'#7C3AED'); g.addColorStop(1,'#DB2777');
      ctx.fillStyle = g; ctx.fillRect(0,0,w,h);
      return c.toDataURL();
    },
  },
  {
    id: 'warmEarth', name: 'Warm Earth', colors: ['#7C2D12', '#B45309'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0,'#431407'); g.addColorStop(0.5,'#7C2D12'); g.addColorStop(1,'#92400E');
      ctx.fillStyle = g; ctx.fillRect(0,0,w,h);
      ctx.beginPath();
      ctx.moveTo(0, h*0.68);
      ctx.bezierCurveTo(w*0.25, h*0.60, w*0.6, h*0.76, w, h*0.62);
      ctx.lineTo(w,h); ctx.lineTo(0,h); ctx.closePath();
      ctx.globalAlpha = 0.75; ctx.fillStyle = '#B45309'; ctx.fill(); ctx.globalAlpha = 1;
      return c.toDataURL();
    },
  },
  {
    id: 'coolSteel', name: 'Cool Steel', colors: ['#0F172A', '#334155'],
    render(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0,'#0F172A'); g.addColorStop(0.5,'#1E293B'); g.addColorStop(1,'#334155');
      ctx.fillStyle = g; ctx.fillRect(0,0,w,h);
      ctx.beginPath();
      ctx.moveTo(w*0.28, 0); ctx.lineTo(w, h*0.72); ctx.lineTo(w, h);
      ctx.lineTo(w*0.12, h); ctx.closePath();
      ctx.fillStyle = 'rgba(100,116,139,0.18)'; ctx.fill();
      return c.toDataURL();
    },
  },
];

// ─── 12 Layout Templates ───────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: 'cleanMinimal', name: 'Clean Minimal', category: 'Simple',
    thumb: { bg: '#ffffff', accent: '#3B82F6', text: '#1e293b' },
    setup: (canvas, fabric, _colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:h*0.75, width:w, height:h*0.25, fill:'#ffffff', opacity:0.92, selectable:false, evented:false }),
        new fabric.Textbox('Your Headline', { left:w*0.06, top:h*0.77, width:w*0.88, fontSize:Math.round(w*0.052), fontWeight:'bold', fontFamily:'Poppins, sans-serif', fill:'#1e293b' }),
        new fabric.Textbox('Subtitle or call-to-action', { left:w*0.06, top:h*0.88, width:w*0.88, fontSize:Math.round(w*0.026), fontFamily:'Poppins, sans-serif', fill:'#64748b' })
      );
    },
  },
  {
    id: 'boldOverlay', name: 'Bold Overlay', category: 'Pro',
    thumb: { bg: '#0f172a', accent: '#3B82F6', text: '#ffffff' },
    setup: (canvas, fabric, _colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:'rgba(0,0,0,0.55)', selectable:false, evented:false }),
        new fabric.Textbox('YOUR BOLD\nHEADLINE', { left:w*0.5, top:h*0.28, width:w*0.88, fontSize:Math.round(w*0.076), fontWeight:'bold', fontFamily:'Montserrat, sans-serif', fill:'#ffffff', textAlign:'center', originX:'center', shadow:'rgba(0,0,0,0.6) 3px 3px 10px' }),
        new fabric.Textbox('Supporting message for your audience', { left:w*0.5, top:h*0.58, width:w*0.88, fontSize:Math.round(w*0.03), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.85)', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'splitLayout', name: 'Split Layout', category: 'Pro',
    thumb: { bg: '#3B82F6', accent: '#1e293b', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:w*0.55, top:0, width:w*0.45, height:h, fill:colors.primary, selectable:false, evented:false }),
        new fabric.Textbox('Your\nHeadline', { left:w*0.6, top:h*0.25, width:w*0.36, fontSize:Math.round(w*0.062), fontWeight:'bold', fontFamily:'Montserrat, sans-serif', fill:'#ffffff' }),
        new fabric.Textbox('Add your key message here.', { left:w*0.6, top:h*0.62, width:w*0.36, fontSize:Math.round(w*0.028), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.85)' })
      );
    },
  },
  {
    id: 'topBand', name: 'Top Band', category: 'Pro',
    thumb: { bg: '#F59E0B', accent: '#ffffff', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h*0.3, fill:colors.primary, selectable:false, evented:false }),
        new fabric.Textbox('TOP HEADLINE', { left:w*0.5, top:h*0.08, width:w*0.88, fontSize:Math.round(w*0.058), fontWeight:'bold', fontFamily:'Oswald, sans-serif', fill:'#ffffff', textAlign:'center', originX:'center', charSpacing:100 }),
        new fabric.Textbox('Your supporting caption', { left:w*0.5, top:h*0.72, width:w*0.88, fontSize:Math.round(w*0.03), fontFamily:'Poppins, sans-serif', fill:'#1e293b', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'quoteCard', name: 'Quote Card', category: 'Elegant',
    thumb: { bg: '#7C3AED', accent: '#a78bfa', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:colors.secondary||'#1e293b', selectable:false, evented:false }),
        new fabric.Textbox('\u201C', { left:w*0.06, top:h*0.06, width:w*0.3, fontSize:Math.round(w*0.18), fontFamily:'Georgia, serif', fill:'rgba(255,255,255,0.15)', selectable:false, evented:false }),
        new fabric.Textbox('"Your inspiring quote goes here.\nMake it memorable."', { left:w*0.5, top:h*0.36, width:w*0.82, fontSize:Math.round(w*0.038), fontFamily:'Playfair Display, serif', fill:'#ffffff', textAlign:'center', originX:'center', lineHeight:1.6, fontStyle:'italic' }),
        new fabric.Textbox('— Name, Position', { left:w*0.5, top:h*0.75, width:w*0.82, fontSize:Math.round(w*0.026), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.7)', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'eventPromo', name: 'Event / Promo', category: 'Promo',
    thumb: { bg: '#DC2626', accent: '#F59E0B', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:'rgba(0,0,0,0.4)', selectable:false, evented:false }),
        new fabric.Circle({ radius:w*0.14, left:w*0.68, top:h*0.04, fill:colors.accent||'#F59E0B', selectable:false, evented:false }),
        new fabric.Textbox('BIG SALE\nEVENT', { left:w*0.5, top:h*0.28, width:w*0.88, fontSize:Math.round(w*0.09), fontWeight:'bold', fontFamily:'Oswald, sans-serif', fill:'#ffffff', textAlign:'center', originX:'center', charSpacing:-50 }),
        new fabric.Rect({ left:w*0.22, top:h*0.70, width:w*0.56, height:h*0.1, fill:colors.primary, rx:8, ry:8, selectable:false, evented:false }),
        new fabric.Textbox('SHOP NOW', { left:w*0.5, top:h*0.72, width:w*0.56, fontSize:Math.round(w*0.038), fontWeight:'bold', fontFamily:'Montserrat, sans-serif', fill:'#ffffff', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'productShowcase', name: 'Showcase', category: 'Pro',
    thumb: { bg: '#f0fdf4', accent: '#16A34A', text: '#1e293b' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:h*0.78, width:w, height:h*0.22, fill:colors.primary||'#16A34A', selectable:false, evented:false }),
        new fabric.Textbox('Product Name', { left:w*0.5, top:h*0.81, width:w*0.88, fontSize:Math.round(w*0.044), fontWeight:'bold', fontFamily:'Poppins, sans-serif', fill:'#ffffff', textAlign:'center', originX:'center' }),
        new fabric.Textbox('Short description · Shop now', { left:w*0.5, top:h*0.91, width:w*0.88, fontSize:Math.round(w*0.024), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.85)', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'testimonial', name: 'Testimonial', category: 'Elegant',
    thumb: { bg: '#0f172a', accent: '#F59E0B', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:'#0f172a', selectable:false, evented:false }),
        new fabric.Textbox('\u2605\u2605\u2605\u2605\u2605', { left:w*0.5, top:h*0.12, width:w*0.88, fontSize:Math.round(w*0.046), fill:colors.accent||'#F59E0B', textAlign:'center', originX:'center', charSpacing:100, selectable:false, evented:false }),
        new fabric.Textbox('"This completely transformed\nmy business. Absolutely\nrecommend!"', { left:w*0.5, top:h*0.30, width:w*0.82, fontSize:Math.round(w*0.034), fontFamily:'Playfair Display, serif', fill:'#ffffff', textAlign:'center', originX:'center', fontStyle:'italic', lineHeight:1.7 }),
        new fabric.Rect({ left:w*0.43, top:h*0.70, width:w*0.14, height:2, fill:colors.accent||'#F59E0B', selectable:false, evented:false }),
        new fabric.Textbox('— Sarah Johnson, CEO', { left:w*0.5, top:h*0.75, width:w*0.88, fontSize:Math.round(w*0.026), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.7)', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'newsletterHeader', name: 'Newsletter', category: 'Pro',
    thumb: { bg: '#334155', accent: '#38BDF8', text: '#ffffff' },
    setup: (canvas, fabric, _colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:h*0.45, width:w, height:h*0.55, selectable:false, evented:false,
          fill: new fabric.Gradient({ type:'linear', gradientUnits:'percentage', coords:{x1:0,y1:0,x2:0,y2:1}, colorStops:[{offset:0,color:'rgba(0,0,0,0)'},{offset:1,color:'rgba(0,0,0,0.85)'}] }) }),
        new fabric.Textbox('Newsletter Title', { left:w*0.06, top:h*0.64, width:w*0.88, fontSize:Math.round(w*0.058), fontWeight:'bold', fontFamily:'Montserrat, sans-serif', fill:'#ffffff' }),
        new fabric.Textbox('Your edition tagline or date', { left:w*0.06, top:h*0.80, width:w*0.88, fontSize:Math.round(w*0.026), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.8)' })
      );
    },
  },
  {
    id: 'simpleFrame', name: 'Simple Frame', category: 'Simple',
    thumb: { bg: '#f8fafc', accent: '#e2e8f0', text: '#1e293b' },
    setup: (canvas, fabric, _colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:8, top:8, width:w-16, height:h*0.78-16, fill:'transparent', stroke:'#ffffff', strokeWidth:8, selectable:false, evented:false }),
        new fabric.Rect({ left:0, top:h*0.78, width:w, height:h*0.22, fill:'#f8fafc', selectable:false, evented:false }),
        new fabric.Textbox('Your caption text here', { left:w*0.5, top:h*0.83, width:w*0.88, fontSize:Math.round(w*0.034), fontFamily:'Poppins, sans-serif', fill:'#1e293b', textAlign:'center', originX:'center' })
      );
    },
  },
  {
    id: 'healthBeauty', name: 'Health & Beauty', category: 'Elegant',
    thumb: { bg: '#fdf2f8', accent: '#ec4899', text: '#831843' },
    setup: (canvas, fabric, _colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:'rgba(253,242,248,0.45)', selectable:false, evented:false }),
        new fabric.Rect({ left:0, top:0, width:w, height:h*0.16, fill:'rgba(253,242,248,0.9)', selectable:false, evented:false }),
        new fabric.Rect({ left:0, top:h*0.76, width:w, height:h*0.24, fill:'rgba(253,242,248,0.92)', selectable:false, evented:false }),
        new fabric.Textbox('Your Brand Name', { left:w*0.5, top:h*0.03, width:w*0.88, fontSize:Math.round(w*0.03), fontFamily:'Josefin Sans, sans-serif', fill:'#9d174d', textAlign:'center', originX:'center', charSpacing:300, fontWeight:'bold' }),
        new fabric.Textbox('Beauty Headline\nGoes Here', { left:w*0.5, top:h*0.79, width:w*0.88, fontSize:Math.round(w*0.044), fontFamily:'Playfair Display, serif', fill:'#831843', textAlign:'center', originX:'center', fontStyle:'italic', lineHeight:1.4 })
      );
    },
  },
  {
    id: 'boldModern', name: 'Bold Modern', category: 'Pro',
    thumb: { bg: '#1e293b', accent: '#F59E0B', text: '#ffffff' },
    setup: (canvas, fabric, colors, w, h) => {
      canvas.add(
        new fabric.Rect({ left:0, top:0, width:w, height:h, fill:'rgba(15,23,42,0.5)', selectable:false, evented:false }),
        new fabric.Polygon([{x:0,y:h*0.55},{x:w*0.65,y:h*0.55},{x:w*0.48,y:h},{x:0,y:h}], { fill:colors.primary, selectable:false, evented:false, opacity:0.9 }),
        new fabric.Textbox('BOLD\nMODERN', { left:w*0.06, top:h*0.58, width:w*0.6, fontSize:Math.round(w*0.088), fontWeight:'bold', fontFamily:'Oswald, sans-serif', fill:'#ffffff', charSpacing:-100, lineHeight:0.9 }),
        new fabric.Textbox('Your tagline or message', { left:w*0.06, top:h*0.84, width:w*0.7, fontSize:Math.round(w*0.026), fontFamily:'Poppins, sans-serif', fill:'rgba(255,255,255,0.85)' })
      );
    },
  },
];

// ─── 14 Text Styles ────────────────────────────────────────────────────────────
const TEXT_STYLES = [
  { id:'heroBold',        name:'Hero Bold',        ff:'Montserrat, sans-serif',    sz:52, fw:'bold',   fill:'#ffffff', shadow:'rgba(0,0,0,0.6) 3px 3px 12px', cs:0,   sample:'HERO BOLD' },
  { id:'cleanModern',     name:'Clean Modern',     ff:'Poppins, sans-serif',       sz:36, fw:'bold',   fill:'#ffffff', shadow:null,                            cs:200, sample:'CLEAN MODERN' },
  { id:'elegantSerif',    name:'Elegant Serif',    ff:'Playfair Display, serif',   sz:40, fw:'normal', fill:'#1e293b', shadow:'rgba(0,0,0,0.15) 1px 1px 6px', cs:50,  sample:'Elegant Serif' },
  { id:'magazineStyle',   name:'Magazine',         ff:'Raleway, sans-serif',       sz:32, fw:'300',    fill:'#ffffff', shadow:null,                            cs:-50, sample:'Magazine Style' },
  { id:'billboard',       name:'Billboard',        ff:'Oswald, sans-serif',        sz:60, fw:'bold',   fill:'#ffffff', shadow:null,                            cs:-100,sample:'BILLBOARD' },
  { id:'retroVintage',    name:'Retro Vintage',    ff:'Bebas Neue, sans-serif',    sz:48, fw:'normal', fill:'#f5e6c8', shadow:'#8B4513 3px 3px 0px',           cs:100, sample:'RETRO VINTAGE' },
  { id:'minimalLabel',    name:'Minimal Label',    ff:'Inter, sans-serif',         sz:18, fw:'normal', fill:'#1e293b', shadow:null,                            cs:400, sample:'MINIMAL LABEL' },
  { id:'techDigital',     name:'Tech Digital',     ff:'Space Grotesk, sans-serif', sz:34, fw:'bold',   fill:'#22d3ee', shadow:'rgba(34,211,238,0.4) 0 0 20px', cs:-50, sample:'Tech Digital' },
  { id:'handwritten',     name:'Handwritten',      ff:'Dancing Script, cursive',   sz:44, fw:'normal', fill:'#92400e', shadow:null,                            cs:0,   sample:'Handwritten Style' },
  { id:'newsHeadline',    name:'News Headline',    ff:'Georgia, serif',            sz:38, fw:'bold',   fill:'#0f172a', shadow:null,                            cs:-30, sample:'News Headline' },
  { id:'socialImpact',    name:'Social Impact',    ff:'Nunito, sans-serif',        sz:40, fw:'800',    fill:'#7c3aed', shadow:null,                            cs:-50, sample:'Social Impact!' },
  { id:'brandStatement',  name:'Brand Statement',  ff:'Josefin Sans, sans-serif',  sz:28, fw:'bold',   fill:'#1e293b', shadow:null,                            cs:300, sample:'BRAND STATEMENT' },
  { id:'luxuryScript',    name:'Luxury Script',    ff:'Great Vibes, cursive',      sz:52, fw:'normal', fill:'#b45309', shadow:'rgba(180,83,9,0.3) 2px 2px 8px',cs:0,   sample:'Luxury Script' },
  { id:'creativeDisplay', name:'Creative Display', ff:'Impact, sans-serif',        sz:56, fw:'normal', fill:'#1e293b', shadow:null,                            cs:0,   sample:'CREATIVE!' },
];

const FONT_FAMILIES = [
  'Poppins','Montserrat','Oswald','Raleway','Inter','Nunito',
  'Playfair Display','Georgia','Great Vibes','Dancing Script',
  'Bebas Neue','Josefin Sans','Space Grotesk','Impact',
];

const SHAPES = [
  { id:'rect',      label:'Rectangle', icon:'far fa-square' },
  { id:'roundRect', label:'Rounded',   icon:'fas fa-square' },
  { id:'circle',    label:'Circle',    icon:'far fa-circle' },
  { id:'triangle',  label:'Triangle',  icon:'fas fa-play' },
  { id:'line',      label:'Line',      icon:'fas fa-minus' },
  { id:'star',      label:'Star',      icon:'fas fa-star' },
  { id:'arrow',     label:'Arrow',     icon:'fas fa-arrow-right' },
  { id:'diamond',   label:'Diamond',   icon:'fas fa-square-full' },
];

const CAT_CHIP = {
  Simple:'bg-slate-100 text-slate-500', Pro:'bg-blue-50 text-blue-600',
  Elegant:'bg-purple-50 text-purple-600', Promo:'bg-rose-50 text-rose-600',
};

// Centre-crop image dataUrl to a target aspect ratio; returns new dataUrl
function cropImageToAspect(dataUrl, targetW, targetH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const iW = img.naturalWidth, iH = img.naturalHeight;
      const ratio = targetW / targetH, imgRatio = iW / iH;
      let sx = 0, sy = 0, sw = iW, sh = iH;
      if (imgRatio > ratio) { sw = iH * ratio;  sx = (iW - sw) / 2; }
      else                  { sh = iW / ratio;  sy = (iH - sh) / 2; }
      const c = document.createElement('canvas');
      c.width = Math.round(sw); c.height = Math.round(sh);
      c.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/png', 0.95));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function TB({ onClick, disabled, title, icon, className='', active=false }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className={`w-7 h-7 flex items-center justify-center rounded-md text-[11px] transition-all disabled:opacity-30 disabled:cursor-not-allowed ${active?'bg-blue-100 text-blue-600':'text-slate-500 hover:text-slate-800 hover:bg-slate-100'} ${className}`}>
      <i className={icon} />
    </button>
  );
}

function SH({ children }) {
  return <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">{children}</p>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function DesignStudio({ day, businessData, baseImageUrl, onSaveDesign, onClose }) {
  const canvasElRef       = useRef(null);
  const fabricRef         = useRef(null);
  const historyRef        = useRef([]);
  const historyIdxRef     = useRef(-1);
  const savingRef         = useRef(false);
  const autoSaveTimerRef  = useRef(null);
  const isFirstMount      = useRef(true);
  // When true, useEffect([baseImg]) skips the bg image load (JSON restore already handled it)
  const suppressBgLoadRef = useRef(false);

  // ── Always-current refs — readable inside setTimeout without stale closures ─
  const baseImgRef        = useRef(baseImageUrl || '');
  const activeTemplateRef = useRef(null);
  const selectedAspectRef = useRef('square');
  const onSaveDesignRef   = useRef(onSaveDesign);
  const dayRef            = useRef(day);

  const [sideTab,        setSideTab]        = useState('image');
  const [selectedAspect, setSelectedAspect] = useState('square');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [selectedObj,    setSelectedObj]    = useState(null);
  const [canvasReady,    setCanvasReady]    = useState(false);
  const [undoLen,        setUndoLen]        = useState(0);
  const [redoLen,        setRedoLen]        = useState(0);
  const [zoom,           setZoom]           = useState(1);
  const [baseImg,        setBaseImg]        = useState(baseImageUrl || '');
  const [textInput,      setTextInput]      = useState('');
  const [selectedStyleId,setSelectedStyleId]= useState('heroBold');
  const [isAiText,       setIsAiText]       = useState(false);
  const [logoDataUrl,    setLogoDataUrl]    = useState('');
  const [objFontSize,    setObjFontSize]    = useState(36);
  const [objFill,        setObjFill]        = useState('#ffffff');
  const [objOpacity,     setObjOpacity]     = useState(1);
  const [objFontFamily,  setObjFontFamily]  = useState('Poppins');
  const [objBold,        setObjBold]        = useState(false);
  const [objItalic,      setObjItalic]      = useState(false);
  const [objAlign,       setObjAlign]       = useState('left');
  const [shapeFill,      setShapeFill]      = useState('#3B82F6');
  const [shapeStroke,    setShapeStroke]    = useState('');
  const [strokeWidth,    setStrokeWidth]    = useState(0);
  const [isSaving,         setIsSaving]         = useState(false);
  const [autoSaveStatus,   setAutoSaveStatus]   = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [isRemovingImgBg,  setIsRemovingImgBg]  = useState(false);
  const [isRemovingLogoBg, setIsRemovingLogoBg] = useState(false);
  const [bgRemoveProgress, setBgRemoveProgress] = useState({ label: '', pct: 0 }); // model download progress

  // ── Sync always-current refs on every render ─────────────────────────────
  baseImgRef.current        = baseImg;
  activeTemplateRef.current = activeTemplate;
  selectedAspectRef.current = selectedAspect;
  onSaveDesignRef.current   = onSaveDesign;
  dayRef.current            = day;

  // ── Images tab state ──────────────────────────────────────────────────────
  const [uploadedImages,  setUploadedImages]  = useState([]);     // { id, dataUrl, name }[]
  const [imgDragOver,     setImgDragOver]     = useState(false);
  const [imgFilter,       setImgFilter]       = useState('none'); // colour filter preset
  const [imgBrightness,   setImgBrightness]   = useState(0);     // -1 … 1
  const [imgContrast,     setImgContrast]     = useState(0);     // -1 … 1
  const [imgSaturation,   setImgSaturation]   = useState(0);     // -1 … 1
  const [imgBlur,         setImgBlur]         = useState(0);     //  0 … 1

  const brandColors = useMemo(() => ({
    primary:   businessData?.brandDna?.primaryColour   || '#3B82F6',
    secondary: businessData?.brandDna?.secondaryColour || '#1E40AF',
    accent:    businessData?.brandDna?.accentColour    || '#F59E0B',
  }), [businessData?.brandDna?.primaryColour, businessData?.brandDna?.secondaryColour, businessData?.brandDna?.accentColour]);

  const CANVAS_W = 500;
  const ratio = ASPECT_RATIOS[selectedAspect] || ASPECT_RATIOS.square;
  const CANVAS_H = Math.round(CANVAS_W * ratio.h / ratio.w);

  useEffect(() => {
    if (document.getElementById('ds-fonts')) return;
    const link = document.createElement('link');
    link.id='ds-fonts'; link.rel='stylesheet';
    link.href='https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;700;800&family=Poppins:wght@300;400;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Raleway:wght@300;400;700&family=Oswald:wght@400;700&family=Bebas+Neue&family=Dancing+Script:wght@400;700&family=Nunito:wght@400;800&family=Josefin+Sans:wght@400;700&family=Great+Vibes&family=Space+Grotesk:wght@400;700&family=Inter:wght@400;700&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!canvasElRef.current || fabricRef.current) return;
    let fabric;
    try { fabric = require('fabric').fabric; } catch (e) { console.error('fabric.js not available:', e); return; }

    // `mounted` guards every async callback so nothing fires after dispose
    let mounted = true;

    const canvas = new fabric.Canvas(canvasElRef.current, { width:CANVAS_W, height:CANVAS_H, backgroundColor:'#f8fafc', preserveObjectStacking:true });
    fabricRef.current = canvas; canvas._fab = fabric;
    const onMod = () => { if (!savingRef.current) saveToHistory(); };
    const onSel = (e) => {
      const o = e.selected?.[0]; setSelectedObj(o||null);
      if (o) {
        setObjFontSize(o.fontSize||36); setObjFill(o.fill||'#ffffff');
        setObjOpacity(o.opacity!==undefined?o.opacity:1);
        if (o.type==='i-text'||o.type==='textbox') {
          setObjFontFamily(o.fontFamily?.split(',')[0]?.trim()||'Poppins');
          setObjBold(o.fontWeight==='bold'||parseInt(o.fontWeight)>=700);
          setObjItalic(o.fontStyle==='italic'); setObjAlign(o.textAlign||'left');
        }
      }
    };
    canvas.on('object:modified',     onMod);
    canvas.on('object:added',        onMod);
    canvas.on('object:removed',      onMod);
    canvas.on('text:editing:exited', onMod); // captures text edits
    canvas.on('selection:created',   onSel);
    canvas.on('selection:updated',   onSel);
    canvas.on('selection:cleared',   () => setSelectedObj(null));

    // ── Restore saved canvas on mount ────────────────────────────────────────
    // Priority: localStorage (most recent edit) → server designData
    const dayKey   = day?.day || 'new';
    const savedStr = localStorage.getItem(`ds_autosave_day${dayKey}`);
    let restoreData = null;
    if (savedStr) { try { restoreData = JSON.parse(savedStr); } catch { /* ignore */ } }
    if (!restoreData?.fabricJSON && day?.designData?.fabricJSON) restoreData = day.designData;

    if (restoreData?.fabricJSON) {
      // loadFromJSON already re-creates the backgroundImage from the stored JSON.
      // We set suppressBgLoadRef so the useEffect([baseImg]) does NOT also fire
      // a second competing setBackgroundImage call, which would cause a race crash.
      if (restoreData.baseImageUrl) {
        suppressBgLoadRef.current = true;       // block redundant bg-load effect
        setBaseImg(restoreData.baseImageUrl);   // update state for export/autosave
      }
      if (restoreData.templateId)  setActiveTemplate(restoreData.templateId);
      if (restoreData.aspectRatio) setSelectedAspect(restoreData.aspectRatio);

      canvas.loadFromJSON(restoreData.fabricJSON, () => {
        if (!mounted) return; // canvas was disposed before callback fired
        canvas.renderAll();
      });
    }

    setCanvasReady(true); saveToHistory(canvas);

    return () => {
      mounted = false;
      // Override ALL Fabric rendering / clearing methods to no-ops BEFORE dispose().
      // loadFromJSON's internal enlivenObjects callback calls canvas.clear() and
      // canvas.renderAll() even after we've started disposing — both must be silenced
      // or they crash with "Cannot read properties of null (reading 'clearRect')".
      try {
        canvas.renderAll         = () => {};
        canvas.clear             = () => {};
        canvas._onObjectAdded    = () => {};
        canvas._onObjectRemoved  = () => {};
      } catch { /* ignore — canvas may already be in a bad state */ }
      fabricRef.current = null;
      try { canvas.dispose(); } catch { /* swallow any remaining dispose errors */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const canvas = fabricRef.current; if (!canvas) return;
    canvas.setWidth(CANVAS_W); canvas.setHeight(CANVAS_H); canvas.renderAll();
  }, [CANVAS_W, CANVAS_H]);

  // Trigger autosave when BG / template / aspect ratio changes (skip initial mount)
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    triggerAutoSave();
  }, [baseImg, activeTemplate, selectedAspect]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip if a JSON restore already handled the background image
    if (suppressBgLoadRef.current) { suppressBgLoadRef.current = false; return; }
    const canvas = fabricRef.current; if (!canvas || !baseImg) return;
    const fabric = canvas._fab;
    let active = true; // prevents post-unmount callbacks
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const fullUrl = baseImg.startsWith('http') || baseImg.startsWith('data:')
      ? baseImg : `${API_URL}${baseImg}`;
    fabric.Image.fromURL(fullUrl, (img) => {
      if (!active || fabricRef.current !== canvas) return;
      canvas.setBackgroundImage(img,
        () => { if (active && fabricRef.current === canvas) canvas.renderAll(); },
        { originX:'left', originY:'top', scaleX:canvas.width/img.width, scaleY:canvas.height/img.height }
      );
    }, { crossOrigin:'anonymous' });
    return () => { active = false; };
  }, [baseImg]);

  // ── triggerAutoSave — defined BEFORE saveToHistory so it's in scope ────────
  // Called directly (no React effect chain). Debounces 2 s, always reads the
  // latest values via refs so stale closures are impossible.
  // toDataURL failure is isolated — JSON always gets saved even if thumbnail fails.
  const triggerAutoSave = useCallback(() => {
    setAutoSaveStatus('saving');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const canvas = fabricRef.current; if (!canvas) return;
      try {
        const json   = canvas.toJSON(['selectable','evented','id']);
        const dayKey = dayRef.current?.day || 'new';

        // toDataURL throws a cross-origin security error when the background
        // is an external URL (e.g. AI-generated image from OpenAI CDN) that
        // doesn't allow canvas reuse. Isolate it so JSON always saves.
        // Fall back to the raw base-image URL so the Preview section still
        // updates instead of going blank.
        let dataUrl = null;
        try { dataUrl = canvas.toDataURL({ format:'png', multiplier:1, quality:0.8 }); }
        catch {
          // Cross-origin canvas taint — use base image URL as preview fallback
          dataUrl = baseImgRef.current || null;
        }

        // 1. Persist to localStorage — canvas survives tab-switching & remounts
        localStorage.setItem(`ds_autosave_day${dayKey}`, JSON.stringify({
          fabricJSON:   json,
          baseImageUrl: baseImgRef.current,
          templateId:   activeTemplateRef.current,
          aspectRatio:  selectedAspectRef.current,
          savedAt:      Date.now(),
        }));

        // 2. Push to parent / server
        if (typeof onSaveDesignRef.current === 'function') {
          onSaveDesignRef.current(
            { fabricJSON: json, baseImageUrl: baseImgRef.current,
              templateId: activeTemplateRef.current, aspectRatio: selectedAspectRef.current },
            dataUrl
          );
        }

        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (err) {
        console.warn('[AutoSave] failed:', err);
        setAutoSaveStatus('idle');
      }
    }, 500); // 500 ms debounce — changes appear in Preview within half a second
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveToHistory = useCallback((canvasArg) => {
    const canvas = canvasArg||fabricRef.current; if (!canvas) return;
    savingRef.current = true;
    const json = canvas.toJSON(['selectable','evented','id']);
    historyRef.current = historyRef.current.slice(0, historyIdxRef.current+1);
    historyRef.current.push(json);
    if (historyRef.current.length>30) historyRef.current.shift();
    historyIdxRef.current = historyRef.current.length-1;
    setUndoLen(historyIdxRef.current); setRedoLen(historyRef.current.length-1-historyIdxRef.current);
    setTimeout(()=>{ savingRef.current=false; }, 100);
    triggerAutoSave();
  }, [triggerAutoSave]);

  const undo = useCallback(() => {
    const canvas=fabricRef.current; if(!canvas||historyIdxRef.current<=0) return;
    historyIdxRef.current--; savingRef.current=true;
    canvas.loadFromJSON(historyRef.current[historyIdxRef.current], ()=>{
      canvas.renderAll(); setUndoLen(historyIdxRef.current); setRedoLen(historyRef.current.length-1-historyIdxRef.current); savingRef.current=false;
    });
  }, []);

  const redo = useCallback(() => {
    const canvas=fabricRef.current; if(!canvas||historyIdxRef.current>=historyRef.current.length-1) return;
    historyIdxRef.current++; savingRef.current=true;
    canvas.loadFromJSON(historyRef.current[historyIdxRef.current], ()=>{
      canvas.renderAll(); setUndoLen(historyIdxRef.current); setRedoLen(historyRef.current.length-1-historyIdxRef.current); savingRef.current=false;
    });
  }, []);

  const applyBgPreset = useCallback((preset) => {
    const canvas=fabricRef.current; if(!canvas) return;
    const fabric=canvas._fab;
    const dataUrl = preset.render(CANVAS_W, CANVAS_H);
    fabric.Image.fromURL(dataUrl, (img)=>{
      if(!fabricRef.current||fabricRef.current!==canvas) return;
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), { scaleX:canvas.width/img.width, scaleY:canvas.height/img.height });
      setBaseImg(dataUrl); saveToHistory();
    });
  }, [CANVAS_W, CANVAS_H, saveToHistory]);

  // Apply template ONLY over the existing background — never clears the BG image/preset
  const applyTemplate = useCallback((template) => {
    const canvas=fabricRef.current; if(!canvas) return;
    const fabric=canvas._fab;
    // Clear only the overlay objects — background image/colour is preserved
    canvas.getObjects().slice().forEach(o=>canvas.remove(o));
    template.setup(canvas, fabric, brandColors, CANVAS_W, CANVAS_H);
    canvas.renderAll(); saveToHistory();
    setActiveTemplate(template.id);
  }, [brandColors, CANVAS_W, CANVAS_H, saveToHistory]);

  // Reset background only (keep all overlay objects)
  const resetBg = useCallback(() => {
    const cv=fabricRef.current; if(!cv) return;
    cv.setBackgroundImage(null, cv.renderAll.bind(cv));
    cv.setBackgroundColor('#f8fafc', ()=>{ cv.renderAll(); saveToHistory(); });
    setBaseImg('');
  }, [saveToHistory]);

  // Reset layout objects only (keep background)
  const resetLayout = useCallback(() => {
    const cv=fabricRef.current; if(!cv) return;
    cv.getObjects().slice().forEach(o=>cv.remove(o));
    cv.renderAll(); setActiveTemplate(null); saveToHistory();
  }, [saveToHistory]);

  // Use Textbox (not IText) — supports word-wrap when box is resized
  const addText = useCallback(() => {
    const canvas=fabricRef.current; if(!canvas) return;
    const fabric=canvas._fab;
    const style=TEXT_STYLES.find(s=>s.id===selectedStyleId)||TEXT_STYLES[0];
    canvas.add(new fabric.Textbox(textInput||style.sample, {
      left:CANVAS_W*0.08, top:CANVAS_H*0.38,
      width:CANVAS_W*0.84,   // draggable corners reflow the text
      fontSize:style.sz, fontWeight:style.fw, fontFamily:style.ff,
      fill:style.fill, charSpacing:style.cs||0, shadow:style.shadow||null, textAlign:'left',
    }));
    canvas.renderAll(); setTextInput('');
  }, [textInput, selectedStyleId, CANVAS_W, CANVAS_H]);

  const applyTextStyle = useCallback((style) => {
    const canvas=fabricRef.current; if(!canvas) return;
    setSelectedStyleId(style.id);
    const active=canvas.getActiveObject();
    if (active&&(active.type==='i-text'||active.type==='textbox')) {
      active.set({ fontSize:style.sz, fontWeight:style.fw, fontFamily:style.ff, fill:style.fill, charSpacing:style.cs||0, shadow:style.shadow||null });
      canvas.renderAll(); saveToHistory();
    }
  }, [saveToHistory]);

  const handleAiText = useCallback(async () => {
    setIsAiText(true);
    try {
      const result = await businessAPI.generateSocialPost({
        dayData:{ postTitle:day?.postTitle||'', contentType:day?.contentType, platform:day?.platform },
        format:'headline_only', maxLength:10,
      });
      const h=result?.postContent?.headline||result?.headline||result?.caption||day?.postTitle||'';
      if (h) setTextInput(h.slice(0,80));
    } catch { if(day?.postTitle) setTextInput(day.postTitle.slice(0,80)); }
    finally { setIsAiText(false); }
  }, [day]);

  // AI Generate → immediately adds text to canvas in one click
  const handleAiQuickAdd = useCallback(async () => {
    const canvas=fabricRef.current; if(!canvas) return;
    setIsAiText(true);
    try {
      const result = await businessAPI.generateSocialPost({
        dayData:{ postTitle:day?.postTitle||'', contentType:day?.contentType, platform:day?.platform },
        format:'headline_only', maxLength:10,
      });
      const h=result?.postContent?.headline||result?.headline||result?.caption||day?.postTitle||'Your Headline';
      const text=(h||'Your Headline').slice(0,100);
      const fabric=canvas._fab;
      const style=TEXT_STYLES.find(s=>s.id===selectedStyleId)||TEXT_STYLES[0];
      canvas.add(new fabric.Textbox(text,{
        left:CANVAS_W*0.08, top:CANVAS_H*0.35, width:CANVAS_W*0.84,
        fontSize:style.sz, fontWeight:style.fw, fontFamily:style.ff,
        fill:style.fill, charSpacing:style.cs||0, shadow:style.shadow||null, textAlign:'left',
      }));
      canvas.renderAll(); saveToHistory();
    } catch { if(day?.postTitle) setTextInput(day.postTitle.slice(0,80)); }
    finally { setIsAiText(false); }
  }, [day, selectedStyleId, CANVAS_W, CANVAS_H, saveToHistory]);

  const addShape = useCallback((shapeId) => {
    const canvas=fabricRef.current; if(!canvas) return;
    const fabric=canvas._fab;
    const cx=CANVAS_W/2, cy=CANVAS_H/2, fill=shapeFill||'#3B82F6', stroke=shapeStroke||undefined, sw=strokeWidth||0;
    let obj;
    switch(shapeId) {
      case 'rect':      obj=new fabric.Rect({left:cx-80,top:cy-40,width:160,height:80,fill,stroke,strokeWidth:sw,rx:0}); break;
      case 'roundRect': obj=new fabric.Rect({left:cx-80,top:cy-40,width:160,height:80,fill,stroke,strokeWidth:sw,rx:16,ry:16}); break;
      case 'circle':    obj=new fabric.Circle({left:cx-60,top:cy-60,radius:60,fill,stroke,strokeWidth:sw}); break;
      case 'triangle':  obj=new fabric.Triangle({left:cx-60,top:cy-60,width:120,height:110,fill,stroke,strokeWidth:sw}); break;
      case 'line':      obj=new fabric.Line([cx-80,cy,cx+80,cy],{stroke:fill,strokeWidth:Math.max(sw,4)}); break;
      case 'star':      obj=new fabric.Path('M 0 -50 L 12 -18 L 47 -18 L 20 4 L 30 38 L 0 18 L -30 38 L -20 4 L -47 -18 L -12 -18 Z',{left:cx,top:cy,fill,stroke,strokeWidth:sw,originX:'center',originY:'center'}); break;
      case 'arrow':     obj=new fabric.Path('M -60 -10 L 20 -10 L 20 -30 L 60 0 L 20 30 L 20 10 L -60 10 Z',{left:cx,top:cy,fill,stroke,strokeWidth:sw,originX:'center',originY:'center'}); break;
      case 'diamond':   obj=new fabric.Polygon([{x:0,y:-60},{x:50,y:0},{x:0,y:60},{x:-50,y:0}],{left:cx,top:cy,fill,stroke,strokeWidth:sw,originX:'center',originY:'center'}); break;
      default: return;
    }
    canvas.add(obj); canvas.setActiveObject(obj); canvas.renderAll(); saveToHistory();
  }, [CANVAS_W, CANVAS_H, shapeFill, shapeStroke, strokeWidth, saveToHistory]);

  const setProp = useCallback((prop, value) => {
    const canvas=fabricRef.current; if(!canvas) return;
    const obj=canvas.getActiveObject(); if(!obj) return;
    obj.set(prop, value); canvas.renderAll();
    if(prop==='fontSize') setObjFontSize(value);
    if(prop==='fill')     setObjFill(value);
    if(prop==='opacity')  setObjOpacity(value);
    if(prop==='fontFamily') setObjFontFamily(value);
    if(prop==='textAlign')  setObjAlign(value);
  }, []);

  const toggleBold = useCallback(()=>{
    const c=fabricRef.current; if(!c) return; const o=c.getActiveObject(); if(!o) return;
    const b=o.fontWeight==='bold'||parseInt(o.fontWeight)>=700;
    o.set('fontWeight',b?'normal':'bold'); c.renderAll(); setObjBold(!b);
  },[]);

  const toggleItalic = useCallback(()=>{
    const c=fabricRef.current; if(!c) return; const o=c.getActiveObject(); if(!o) return;
    const i=o.fontStyle==='italic'; o.set('fontStyle',i?'normal':'italic'); c.renderAll(); setObjItalic(!i);
  },[]);

  const deleteSelected = useCallback(()=>{
    const c=fabricRef.current; if(!c) return; const o=c.getActiveObject();
    if(o){c.remove(o);c.discardActiveObject();c.renderAll();saveToHistory();}
  },[saveToHistory]);

  const duplicateSelected = useCallback(()=>{
    const c=fabricRef.current; if(!c) return; const o=c.getActiveObject(); if(!o) return;
    o.clone((cl)=>{cl.set({left:o.left+20,top:o.top+20});c.add(cl);c.setActiveObject(cl);c.renderAll();saveToHistory();});
  },[saveToHistory]);

  const flipObj = useCallback((dir)=>{
    const c=fabricRef.current; if(!c) return; const o=c.getActiveObject(); if(!o) return;
    if(dir==='h') o.set('flipX',!o.flipX); else o.set('flipY',!o.flipY);
    c.renderAll(); saveToHistory();
  },[saveToHistory]);

  const bringToFront = useCallback(()=>{ const c=fabricRef.current; const o=c?.getActiveObject(); if(c&&o){c.bringToFront(o);c.renderAll();} },[]);
  const sendToBack   = useCallback(()=>{ const c=fabricRef.current; const o=c?.getActiveObject(); if(c&&o){c.sendToBack(o);c.renderAll();} },[]);

  // ── Helper: blob → base64 data URL ───────────────────────────────────────────
  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

  // ── RMBG-1.4 progress callback (shared) ──────────────────────────────────────
  const rmbgProgress = (key, current, total) => {
    const pct   = total > 0 ? Math.round((current / total) * 100) : 0;
    const label = key.includes('fetch') || key.includes('load') ? `Downloading model… ${pct}%` : `Removing background… ${pct}%`;
    setBgRemoveProgress({ label, pct });
  };

  // ── RMBG-1.4: remove bg from selected CANVAS IMAGE ───────────────────────────
  const handleRemoveImageBg = useCallback(async () => {
    const canvas = fabricRef.current;
    const obj    = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    setIsRemovingImgBg(true);
    setBgRemoveProgress({ label: 'Starting…', pct: 0 });
    try {
      const srcUrl = obj._srcDataUrl || obj._element?.src || '';
      if (!srcUrl) throw new Error('No source image found on selected object');
      console.log('[DesignStudio] Running RMBG-1.4 on canvas image…');
      const fetchRes  = await fetch(srcUrl);
      const inputBlob = await fetchRes.blob();
      const resultBlob = await rmbgAI(inputBlob, {
        progress: rmbgProgress,
        output: { quality: 0.95, format: 'image/png' },
      });
      const resultDataUrl = await blobToDataUrl(resultBlob);
      const fabric = canvas._fab;
      fabric.Image.fromURL(resultDataUrl, (newImg) => {
        if (!fabricRef.current) return;
        newImg.set({
          left: obj.left, top: obj.top,
          scaleX: obj.scaleX, scaleY: obj.scaleY,
          angle: obj.angle, flipX: obj.flipX, flipY: obj.flipY,
          opacity: obj.opacity,
          _srcDataUrl: resultDataUrl,
        });
        canvas.remove(obj);
        canvas.add(newImg);
        canvas.setActiveObject(newImg);
        canvas.renderAll();
        saveToHistory();
      }, { crossOrigin: 'anonymous' });
      console.log('[DesignStudio] ✅ Background removed successfully');
    } catch (err) {
      console.error('[DesignStudio] RMBG failed:', err.message || err);
      alert('Background removal failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIsRemovingImgBg(false);
      setBgRemoveProgress({ label: '', pct: 0 });
    }
  }, [saveToHistory]);

  // ── RMBG-1.4: remove bg from LOGO in Brand tab ───────────────────────────────
  const handleRemoveLogoBg = useCallback(async () => {
    if (!logoDataUrl) return;
    setIsRemovingLogoBg(true);
    setBgRemoveProgress({ label: 'Starting…', pct: 0 });
    try {
      console.log('[DesignStudio] Running RMBG-1.4 on logo…');
      const fetchRes  = await fetch(logoDataUrl);
      const inputBlob = await fetchRes.blob();
      const resultBlob = await rmbgAI(inputBlob, {
        progress: rmbgProgress,
        output: { quality: 0.95, format: 'image/png' },
      });
      const resultDataUrl = await blobToDataUrl(resultBlob);
      setLogoDataUrl(resultDataUrl);
      console.log('[DesignStudio] ✅ Logo background removed successfully');
    } catch (err) {
      console.error('[DesignStudio] Logo RMBG failed:', err.message || err);
      alert('Background removal failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIsRemovingLogoBg(false);
      setBgRemoveProgress({ label: '', pct: 0 });
    }
  }, [logoDataUrl]);

  const alignObj = useCallback((type)=>{
    const canvas=fabricRef.current; if(!canvas) return;
    const obj=canvas.getActiveObject(); if(!obj) return;
    const w=canvas.width, h=canvas.height, bw=obj.getBoundingRect().width, bh=obj.getBoundingRect().height;
    switch(type){
      case 'left':    obj.set('left',0); break;
      case 'hcenter': obj.set('left',(w-bw)/2); break;
      case 'right':   obj.set('left',w-bw); break;
      case 'top':     obj.set('top',0); break;
      case 'vcenter': obj.set('top',(h-bh)/2); break;
      case 'bottom':  obj.set('top',h-bh); break;
      default: break;
    }
    obj.setCoords(); canvas.renderAll(); saveToHistory();
  },[saveToHistory]);

  useEffect(() => {
    const h=(e)=>{
      if(e.ctrlKey&&e.key==='z'){e.preventDefault();undo();}
      if(e.ctrlKey&&e.key==='y'){e.preventDefault();redo();}
      if(e.ctrlKey&&e.key==='d'){e.preventDefault();duplicateSelected();}
      const t=fabricRef.current?.getActiveObject()?.type;
      if((e.key==='Delete'||e.key==='Backspace')&&t!=='i-text'&&t!=='textbox'){e.preventDefault();deleteSelected();}
    };
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  },[undo,redo,duplicateSelected,deleteSelected]);

  const handleLogoUpload = useCallback((e)=>{
    const f=e.target.files?.[0]; if(!f) return;
    const r=new FileReader(); r.onload=(ev)=>setLogoDataUrl(ev.target.result); r.readAsDataURL(f);
  },[]);

  const addLogoToCanvas = useCallback(()=>{
    const canvas=fabricRef.current; if(!canvas||!logoDataUrl) return;
    const fabric=canvas._fab;
    fabric.Image.fromURL(logoDataUrl,(img)=>{
      if(!fabricRef.current||fabricRef.current!==canvas) return;
      if(img.width>CANVAS_W*0.22) img.scaleToWidth(CANVAS_W*0.22);
      img.set({left:CANVAS_W*0.04,top:CANVAS_H*0.04});
      canvas.add(img); canvas.setActiveObject(img); canvas.renderAll(); saveToHistory();
    },{crossOrigin:'anonymous'});
  },[logoDataUrl,CANVAS_W,CANVAS_H,saveToHistory]);

  const handleBaseUpload = useCallback((e)=>{
    const f=e.target.files?.[0]; if(!f) return;
    const r=new FileReader(); r.onload=(ev)=>setBaseImg(ev.target.result); r.readAsDataURL(f);
  },[]);

  // ── Images tab handlers ───────────────────────────────────────────────────

  // Upload one or many image files → add to the local gallery
  const handleImageUpload = useCallback((e) => {
    const files = e.target?.files || e.dataTransfer?.files;
    if (!files || !files.length) return;
    Array.from(files).slice(0, 10).forEach((f) => {
      const r = new FileReader();
      r.onload = (ev) => {
        const id = `img_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
        setUploadedImages(prev => [{ id, dataUrl: ev.target.result, name: f.name }, ...prev].slice(0, 30));
      };
      r.readAsDataURL(f);
    });
  }, []);

  // Place an image from the gallery onto the Fabric canvas as a draggable object
  const addImageToCanvas = useCallback((dataUrl) => {
    const canvas = fabricRef.current; if (!canvas) return;
    const fabric = canvas._fab;
    fabric.Image.fromURL(dataUrl, (img) => {
      if (!fabricRef.current || fabricRef.current !== canvas) return;
      const maxW = CANVAS_W * 0.8, maxH = CANVAS_H * 0.8;
      if (img.width > maxW) img.scaleToWidth(maxW);
      if (img.getScaledHeight() > maxH) img.scaleToHeight(maxH);
      img.set({
        left: (CANVAS_W - img.getScaledWidth()) / 2,
        top:  (CANVAS_H - img.getScaledHeight()) / 2,
        _srcDataUrl: dataUrl, // store for bg-removal / crop
      });
      canvas.add(img); canvas.setActiveObject(img); canvas.renderAll(); saveToHistory();
      setSideTab('images'); // jump to Images tab so edit panel is visible
    }, { crossOrigin: 'anonymous' });
  }, [CANVAS_W, CANVAS_H, saveToHistory]);


  // Centre-crop the selected canvas image to a target aspect ratio
  const handleCropImage = useCallback(async (ratioW, ratioH) => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    const srcUrl = obj._srcDataUrl || obj._element?.src || '';
    if (!srcUrl) return;
    const cropped = await cropImageToAspect(srcUrl, ratioW, ratioH);
    const prevW = obj.getScaledWidth();
    const fabric = canvas._fab;
    fabric.Image.fromURL(cropped, (newImg) => {
      if (!fabricRef.current) return;
      newImg.scaleToWidth(prevW);
      newImg.set({
        left: obj.left, top: obj.top,
        angle: obj.angle, flipX: obj.flipX, flipY: obj.flipY,
        opacity: obj.opacity, _srcDataUrl: cropped,
      });
      canvas.remove(obj); canvas.add(newImg);
      canvas.setActiveObject(newImg); canvas.renderAll(); saveToHistory();
    }, { crossOrigin: 'anonymous' });
  }, [saveToHistory]);

  // Apply colour filter preset + manual sliders to selected canvas image via Fabric filters
  const applyImageFilters = useCallback(() => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    const F = canvas._fab.Image.filters;
    const filters = [];
    // Colour filter preset
    if      (imgFilter === 'bw')      { filters.push(new F.Grayscale()); }
    else if (imgFilter === 'sepia')   { filters.push(new F.Sepia()); }
    else if (imgFilter === 'vintage') {
      filters.push(new F.Sepia());
      filters.push(new F.Saturation({ saturation: -0.25 }));
      filters.push(new F.Brightness({ brightness:  0.05 }));
    }
    else if (imgFilter === 'cool')    {
      filters.push(new F.HueRotation({ rotation: 0.4 }));
      filters.push(new F.Saturation({ saturation: -0.1 }));
    }
    else if (imgFilter === 'warm')    {
      filters.push(new F.HueRotation({ rotation: -0.3 }));
      filters.push(new F.Saturation({ saturation:  0.15 }));
      filters.push(new F.Brightness({ brightness:  0.04 }));
    }
    else if (imgFilter === 'faded')   {
      filters.push(new F.Brightness({ brightness:  0.18 }));
      filters.push(new F.Contrast({ contrast:     -0.22 }));
      filters.push(new F.Saturation({ saturation: -0.20 }));
    }
    else if (imgFilter === 'vivid')   {
      filters.push(new F.Saturation({ saturation:  0.45 }));
      filters.push(new F.Contrast({ contrast:      0.18 }));
    }
    // Manual adjustment sliders
    if (imgBrightness !== 0) filters.push(new F.Brightness({ brightness: imgBrightness }));
    if (imgContrast   !== 0) filters.push(new F.Contrast({ contrast:     imgContrast   }));
    if (imgSaturation !== 0) filters.push(new F.Saturation({ saturation: imgSaturation }));
    if (imgBlur       > 0)   filters.push(new F.Blur({ blur:             imgBlur       }));

    obj.filters = filters;
    obj.applyFilters();
    canvas.renderAll();
    saveToHistory();
  }, [imgFilter, imgBrightness, imgContrast, imgSaturation, imgBlur, saveToHistory]);

  const addUrlToCanvas = useCallback(()=>{
    const val=document.getElementById('ds-url-input')?.value; if(!val) return;
    const canvas=fabricRef.current; if(!canvas) return;
    const fabric=canvas._fab;
    canvas.add(new fabric.Textbox(val,{ left:CANVAS_W*0.5, top:CANVAS_H*0.92, originX:'center', width:CANVAS_W*0.8, fontSize:Math.round(CANVAS_W*0.022), fontFamily:'Poppins, sans-serif', fill:'#ffffff', textAlign:'center', opacity:0.85 }));
    canvas.renderAll(); saveToHistory();
  },[CANVAS_W,CANVAS_H,saveToHistory]);

  const handleExportAndSave = useCallback(async()=>{
    const canvas=fabricRef.current; if(!canvas) return;
    setIsSaving(true);
    try {
      const dataUrl=canvas.toDataURL({format:'png',multiplier:2,quality:1});
      let exportedImageUrl=dataUrl;
      try {
        const res=await businessAPI.saveImageToLibrary({ imageData:dataUrl, fileName:`design_day${day?.day||'new'}_${Date.now()}.png`, category:'social', description:day?.postTitle||'Design Studio export' });
        if(res?.success&&res?.imageUrl) exportedImageUrl=res.imageUrl;
      } catch(e){ console.warn('Asset upload failed:',e.message); }
      onSaveDesign({ fabricJSON:canvas.toJSON(['selectable','evented','id']), baseImageUrl:baseImg, templateId:activeTemplate, aspectRatio:selectedAspect }, exportedImageUrl);
    } finally { setIsSaving(false); }
  },[baseImg,activeTemplate,selectedAspect,onSaveDesign,day]);

  const clearCanvas = useCallback(()=>{
    const c=fabricRef.current; if(!c) return;
    c.clear(); c.setBackgroundColor('#f8fafc',c.renderAll.bind(c));
    setBaseImg(''); setActiveTemplate(null); saveToHistory();
  },[saveToHistory]);

  const getFullUrl=(url)=>{
    if(!url) return ''; if(url.startsWith('http')||url.startsWith('data:')) return url;
    return `${process.env.REACT_APP_API_URL||'http://localhost:5000'}${url}`;
  };

  const isText = selectedObj?.type==='i-text'||selectedObj?.type==='textbox';
  const hasObj = !!selectedObj;

  return (
    <div className="flex flex-col bg-white border-t border-slate-200" style={{ height:'calc(100vh - 128px)', minHeight:520 }}>

      {/* TOOLBAR */}
      <div className="flex-shrink-0 h-11 bg-white border-b border-slate-200 flex items-center gap-1 px-3 overflow-x-auto">
        {/* Aspect */}
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5 flex-shrink-0 mr-1">
          {Object.entries(ASPECT_RATIOS).map(([key,val])=>(
            <button key={key} onClick={()=>setSelectedAspect(key)} title={val.hint}
              className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all ${selectedAspect===key?'bg-white text-slate-900 shadow-sm':'text-slate-400 hover:text-slate-700'}`}>{val.label}</button>
          ))}
        </div>
        <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0" />
        <TB onClick={undo} disabled={undoLen<=0} title="Undo (Ctrl+Z)" icon="fas fa-undo" />
        <TB onClick={redo} disabled={redoLen<=0} title="Redo (Ctrl+Y)" icon="fas fa-redo" />
        <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0" />
        <TB onClick={deleteSelected}    disabled={!hasObj} title="Delete"           icon="fas fa-trash"              className="hover:!text-red-500" />
        <TB onClick={duplicateSelected} disabled={!hasObj} title="Duplicate (Ctrl+D)" icon="fas fa-copy" />
        <TB onClick={bringToFront}      disabled={!hasObj} title="Bring to Front"   icon="fas fa-angle-double-up" />
        <TB onClick={sendToBack}        disabled={!hasObj} title="Send to Back"     icon="fas fa-angle-double-down" />
        <TB onClick={()=>flipObj('h')}  disabled={!hasObj} title="Flip Horizontal"  icon="fas fa-arrows-left-right" />
        <TB onClick={()=>flipObj('v')}  disabled={!hasObj} title="Flip Vertical"    icon="fas fa-arrows-up-down" />
        <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0" />
        <TB onClick={()=>alignObj('left')}    disabled={!hasObj} title="Align Left"   icon="fas fa-align-left" />
        <TB onClick={()=>alignObj('hcenter')} disabled={!hasObj} title="Centre H"     icon="fas fa-align-center" />
        <TB onClick={()=>alignObj('right')}   disabled={!hasObj} title="Align Right"  icon="fas fa-align-right" />
        <TB onClick={()=>alignObj('top')}     disabled={!hasObj} title="Align Top"    icon="fas fa-arrow-up" />
        <TB onClick={()=>alignObj('vcenter')} disabled={!hasObj} title="Centre V"     icon="fas fa-arrows-up-down-left-right" />
        <TB onClick={()=>alignObj('bottom')}  disabled={!hasObj} title="Align Bottom" icon="fas fa-arrow-down" />
        {isText && (<>
          <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0" />
          <select value={objFontFamily} onChange={(e)=>{ setProp('fontFamily',e.target.value+', sans-serif'); setObjFontFamily(e.target.value); }}
            className="h-7 px-1 text-[10px] bg-slate-50 border border-slate-200 rounded-md text-slate-700 focus:outline-none focus:border-blue-400 max-w-[110px]">
            {FONT_FAMILIES.map(f=><option key={f} value={f}>{f}</option>)}
          </select>
          <input type="number" min="6" max="300" value={objFontSize} onChange={(e)=>setProp('fontSize',parseInt(e.target.value))}
            className="w-12 h-7 px-1 text-[10px] bg-slate-50 border border-slate-200 rounded-md text-slate-700 focus:outline-none focus:border-blue-400" />
          <TB onClick={toggleBold}   title="Bold"   icon="fas fa-bold"   active={objBold} />
          <TB onClick={toggleItalic} title="Italic" icon="fas fa-italic" active={objItalic} />
          <TB onClick={()=>setProp('textAlign','left')}   title="Left"   icon="fas fa-align-left"   active={objAlign==='left'} />
          <TB onClick={()=>setProp('textAlign','center')} title="Centre" icon="fas fa-align-center" active={objAlign==='center'} />
          <TB onClick={()=>setProp('textAlign','right')}  title="Right"  icon="fas fa-align-right"  active={objAlign==='right'} />
          <input type="color" value={objFill.startsWith('#')?objFill:'#ffffff'} onChange={(e)=>setProp('fill',e.target.value)} title="Text colour"
            className="w-7 h-7 rounded-md cursor-pointer border border-slate-200 p-0.5 bg-white" />
        </>)}
        {hasObj && (<>
          <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0" />
          <input type="range" min="0.1" max="1" step="0.05" value={objOpacity} onChange={(e)=>setProp('opacity',parseFloat(e.target.value))}
            className="accent-blue-500 flex-shrink-0" style={{width:64}} />
          <span className="text-[9px] text-slate-400 w-7 flex-shrink-0">{Math.round(objOpacity*100)}%</span>
        </>)}
        <div className="flex-1" />
        <div className="w-px h-5 bg-slate-200 mx-1 flex-shrink-0" />
        <TB onClick={clearCanvas} title="Clear Canvas" icon="fas fa-eraser" />
        <button onClick={handleExportAndSave} disabled={isSaving}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-lg transition-all disabled:opacity-60 shadow-sm ml-1 flex-shrink-0">
          <i className={`fas ${isSaving?'fa-spinner fa-spin':'fa-save'} text-[10px]`} />{isSaving?'Saving…':'Save Design'}
        </button>
        <button onClick={() => {
          // Flush any pending debounced auto-save immediately before navigating away
          if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
          const canvas = fabricRef.current;
          if (canvas && typeof onSaveDesignRef.current === 'function') {
            try {
              const json = canvas.toJSON(['selectable','evented','id']);
              let dataUrl = null;
              try { dataUrl = canvas.toDataURL({ format:'png', multiplier:1, quality:0.8 }); }
              catch { dataUrl = baseImgRef.current || null; }
              onSaveDesignRef.current(
                { fabricJSON: json, baseImageUrl: baseImgRef.current,
                  templateId: activeTemplateRef.current, aspectRatio: selectedAspectRef.current },
                dataUrl
              );
            } catch { /* canvas may be in transition — ignore */ }
          }
          onClose();
        }} title="Close Studio"
          className="ml-1 w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all flex-shrink-0">
          <i className="fas fa-times text-sm" />
        </button>
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden">

        {/* ALWAYS-VISIBLE SIDEBAR (280px) */}
        <div className="flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden" style={{width:280}}>

          {/* Tab row */}
          <div className="flex-shrink-0 flex border-b border-slate-200 bg-slate-50">
            {[
              {id:'image',  icon:'fas fa-image',        label:'BG'},
              {id:'layout', icon:'fas fa-th-large',     label:'Layout'},
              {id:'images', icon:'fas fa-photo-film',   label:'Images'},
              {id:'text',   icon:'fas fa-font',         label:'Text'},
              {id:'shapes', icon:'fas fa-shapes',       label:'Shapes'},
              {id:'brand',  icon:'fas fa-paint-brush',  label:'Brand'},
            ].map(t=>(
              <button key={t.id} onClick={()=>setSideTab(t.id)} title={t.label}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[7.5px] font-bold tracking-wide transition-all border-b-2 ${sideTab===t.id?'border-blue-500 text-blue-600 bg-white':'border-transparent text-slate-400 hover:text-slate-600 hover:bg-white'}`}>
                <i className={`${t.icon} text-[11px]`} /><span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">

            {/* BG TAB — 12 predefined presets */}
            {sideTab==='image' && (
              <div className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <SH>Background Presets</SH>
                  {baseImg && (
                    <button onClick={resetBg} className="text-[9px] text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5 mb-2">
                      <i className="fas fa-times-circle text-[8px]" />Clear
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {BG_PRESETS.map(p=>(
                    <button key={p.id} onClick={()=>applyBgPreset(p)} title={p.name}
                      className={`flex flex-col items-center gap-1 group ${baseImg && baseImg.includes(p.id) ? 'ring-2 ring-blue-500 rounded-xl' : ''}`}>
                      <div className="w-full aspect-square rounded-xl overflow-hidden border-2 border-transparent group-hover:border-blue-400 transition-all shadow-sm"
                        style={{background:`linear-gradient(135deg,${p.colors[0]},${p.colors[1]})`}} />
                      <span className="text-[8px] text-slate-500 group-hover:text-blue-600 font-medium text-center leading-tight">{p.name}</span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-slate-100 pt-3">
                  <SH>Upload Your Own</SH>
                  <label className="flex items-center justify-center gap-1.5 w-full py-2.5 border-2 border-dashed border-slate-200 hover:border-blue-400 text-slate-500 hover:text-blue-600 text-[10px] font-semibold rounded-xl cursor-pointer hover:bg-blue-50 transition-all">
                    <i className="fas fa-cloud-upload-alt text-[11px]" />Upload Image
                    <input type="file" accept="image/*" className="hidden" onChange={handleBaseUpload} />
                  </label>
                </div>
                {baseImg && (
                  <div className="border-t border-slate-100 pt-2">
                    <SH>Active Background</SH>
                    <div className="relative rounded-xl overflow-hidden border-2 border-blue-200 aspect-video bg-slate-100">
                      <img src={getFullUrl(baseImg)} alt="bg" className="w-full h-full object-cover" onError={e=>{e.target.style.display='none';}} />
                      <button onClick={resetBg}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors">
                        <i className="fas fa-times text-white text-[8px]" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/60 rounded px-1.5 py-0.5 text-[8px] text-white font-medium">Active</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* LAYOUT TAB */}
            {sideTab==='layout' && (
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <SH>12 Templates</SH>
                  {activeTemplate && (
                    <button onClick={resetLayout} className="text-[9px] text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5 mb-2">
                      <i className="fas fa-times-circle text-[8px]" />Reset
                    </button>
                  )}
                </div>
                <p className="text-[9px] text-slate-400 mb-2 leading-tight">Templates layer on top of your background — pick BG first, then layout.</p>
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATES.map(tpl=>(
                    <button key={tpl.id} onClick={()=>applyTemplate(tpl)}
                      className={`relative flex flex-col items-center gap-1 p-1.5 rounded-xl border transition-all ${activeTemplate===tpl.id?'border-blue-500 bg-blue-50 ring-1 ring-blue-200 shadow-sm':'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}>
                      <div className="w-full aspect-square rounded-lg overflow-hidden relative" style={{background:tpl.thumb.bg}}>
                        <div className="absolute inset-0 flex flex-col justify-end p-1.5">
                          <div className="h-2 rounded-sm mb-1" style={{background:tpl.thumb.accent,opacity:0.9,width:'80%'}} />
                          <div className="h-1 rounded-sm" style={{background:tpl.thumb.text,opacity:0.5,width:'55%'}} />
                        </div>
                        {activeTemplate===tpl.id && (
                          <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shadow">
                            <i className="fas fa-check text-white text-[7px]" />
                          </div>
                        )}
                      </div>
                      <p className="text-[8.5px] font-semibold text-slate-700 leading-tight truncate w-full text-center">{tpl.name}</p>
                      <span className={`text-[7px] px-1.5 py-0.5 rounded-full font-bold ${CAT_CHIP[tpl.category]}`}>{tpl.category}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ─── IMAGES TAB ──────────────────────────────────────────────── */}
            {sideTab==='images' && (() => {
              const isImgSelected = selectedObj?.type === 'image';
              const IMG_FILTERS = [
                { id:'none',    label:'None',    dot:'#94A3B8' },
                { id:'bw',      label:'B&W',     dot:'#374151' },
                { id:'sepia',   label:'Sepia',   dot:'#92400E' },
                { id:'vintage', label:'Vintage', dot:'#B45309' },
                { id:'cool',    label:'Cool',    dot:'#0284C7' },
                { id:'warm',    label:'Warm',    dot:'#EA580C' },
                { id:'faded',   label:'Faded',   dot:'#6B7280' },
                { id:'vivid',   label:'Vivid',   dot:'#7C3AED' },
              ];
              return (
                <div className="p-3 space-y-4">

                  {/* ── Drag & Drop Upload Area ────────────────────────────── */}
                  <div>
                    <SH>Upload Photos</SH>
                    <div
                      onDragOver={e=>{ e.preventDefault(); setImgDragOver(true); }}
                      onDragLeave={()=>setImgDragOver(false)}
                      onDrop={e=>{ e.preventDefault(); setImgDragOver(false); handleImageUpload({ target:{ files: e.dataTransfer.files } }); }}
                      className={`relative border-2 border-dashed rounded-2xl p-4 transition-all text-center ${imgDragOver?'border-blue-400 bg-blue-50/70':'border-slate-200 hover:border-blue-300 hover:bg-slate-50/60'}`}
                    >
                      <div className={`w-9 h-9 mx-auto mb-2 rounded-xl flex items-center justify-center transition-colors ${imgDragOver?'bg-blue-100':'bg-slate-100'}`}>
                        <i className={`fas fa-cloud-upload-alt text-lg transition-colors ${imgDragOver?'text-blue-500':'text-slate-400'}`} />
                      </div>
                      <p className="text-[10px] font-semibold text-slate-600 mb-1">
                        {imgDragOver ? 'Drop to upload' : 'Drop photos here'}
                      </p>
                      <p className="text-[8.5px] text-slate-400 mb-3">PNG · JPG · WebP · HEIC</p>
                      <label className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-xl cursor-pointer transition-all shadow-sm shadow-blue-200">
                        <i className="fas fa-folder-open text-[9px]" />Browse Files
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                      </label>
                    </div>
                  </div>

                  {/* ── Photo Gallery ────────────────────────────────────────── */}
                  {uploadedImages.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <SH>Your Photos</SH>
                        <span className="text-[8px] text-slate-400 font-medium mb-2">{uploadedImages.length} photo{uploadedImages.length!==1?'s':''}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {uploadedImages.map(img=>(
                          <div
                            key={img.id}
                            className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200 cursor-pointer"
                            onClick={()=>addImageToCanvas(img.dataUrl)}
                          >
                            <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
                            {/* Hover action overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-150 flex items-center justify-center gap-1 pointer-events-none group-hover:pointer-events-auto">
                              <button
                                onClick={e=>{ e.stopPropagation(); addImageToCanvas(img.dataUrl); }}
                                title="Add to canvas"
                                className="opacity-0 group-hover:opacity-100 transition-all w-7 h-7 bg-white rounded-xl flex items-center justify-center shadow hover:bg-blue-50 hover:scale-105"
                              ><i className="fas fa-plus text-blue-600 text-[10px]" /></button>
                              <button
                                onClick={e=>{ e.stopPropagation(); setBaseImg(img.dataUrl); }}
                                title="Set as background"
                                className="opacity-0 group-hover:opacity-100 transition-all w-7 h-7 bg-white rounded-xl flex items-center justify-center shadow hover:bg-emerald-50 hover:scale-105"
                              ><i className="fas fa-expand text-emerald-600 text-[10px]" /></button>
                              <button
                                onClick={e=>{ e.stopPropagation(); setUploadedImages(p=>p.filter(i=>i.id!==img.id)); }}
                                title="Remove from library"
                                className="opacity-0 group-hover:opacity-100 transition-all w-7 h-7 bg-white rounded-xl flex items-center justify-center shadow hover:bg-red-50 hover:scale-105"
                              ><i className="fas fa-trash text-red-400 text-[10px]" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[8px] text-slate-300 mt-1.5 text-center">Click to add · Hover for options</p>
                    </div>
                  )}

                  {/* ── Empty gallery prompt ─────────────────────────────────── */}
                  {uploadedImages.length === 0 && !isImgSelected && (
                    <div className="text-center py-4">
                      <i className="fas fa-images text-slate-200 text-3xl mb-2 block" />
                      <p className="text-[10px] text-slate-400 font-medium">Your photo library is empty</p>
                      <p className="text-[9px] text-slate-300 mt-0.5 leading-tight">Upload images to place in your design or use as backgrounds</p>
                    </div>
                  )}

                  {/* ── Image Edit Panel (canvas image selected) ─────────────── */}
                  {isImgSelected && (
                    <div className="border-t border-slate-100 pt-3 space-y-4">

                      {/* Selected indicator */}
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="w-5 h-5 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-image text-white text-[8px]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9px] font-bold text-blue-700 leading-none">Image selected</p>
                          <p className="text-[8px] text-blue-400 mt-0.5">Edit tools below</p>
                        </div>
                      </div>

                      {/* Remove Background — BRIAAI RMBG-1.4 (browser, free) ─ */}
                      <div>
                        <SH>Remove Background</SH>
                        <button
                          onClick={handleRemoveImageBg}
                          disabled={isRemovingImgBg}
                          className="w-full py-2.5 text-[10px] font-bold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 rounded-xl flex flex-col items-center justify-center gap-1 transition-all shadow-sm shadow-indigo-200"
                        >
                          {isRemovingImgBg ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <i className="fas fa-circle-notch fa-spin text-[10px]" />
                                <span>{bgRemoveProgress.label || 'Processing…'}</span>
                              </div>
                              {bgRemoveProgress.pct > 0 && (
                                <div className="w-4/5 bg-white/25 rounded-full h-1 mt-0.5">
                                  <div className="bg-white rounded-full h-1 transition-all duration-300" style={{ width: `${bgRemoveProgress.pct}%` }} />
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <i className="fas fa-wand-magic-sparkles text-[10px]" />
                              Remove Background (AI)
                            </div>
                          )}
                        </button>
                        <p className="text-[8px] text-slate-400 mt-1.5 text-center flex items-center justify-center gap-1">
                          <i className="fas fa-microchip text-violet-400 text-[7px]" />
                          BRIAAI RMBG-1.4 · runs in browser · free
                        </p>
                      </div>

                      {/* Crop ─────────────────────────────────────────────── */}
                      <div>
                        <SH>Crop to Ratio</SH>
                        <div className="grid grid-cols-5 gap-1">
                          {[
                            { label:'1:1',  w:1,  h:1  },
                            { label:'4:5',  w:4,  h:5  },
                            { label:'9:16', w:9,  h:16 },
                            { label:'16:9', w:16, h:9  },
                            { label:'3:2',  w:3,  h:2  },
                          ].map(c=>(
                            <button
                              key={c.label}
                              onClick={()=>handleCropImage(c.w, c.h)}
                              className="px-0.5 py-2 text-[9px] font-bold border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-all text-slate-600 text-center"
                            >{c.label}</button>
                          ))}
                        </div>
                        <p className="text-[8px] text-slate-300 mt-1">Centre-crops · undo with Ctrl+Z</p>
                      </div>

                      {/* Filters ─────────────────────────────────────────── */}
                      <div>
                        <SH>Colour Filter</SH>
                        <div className="grid grid-cols-4 gap-1">
                          {IMG_FILTERS.map(f=>(
                            <button
                              key={f.id}
                              onClick={()=>setImgFilter(f.id)}
                              className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[8.5px] font-bold transition-all ${
                                imgFilter===f.id
                                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{background:f.dot}} />
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Adjustments ─────────────────────────────────────── */}
                      <div>
                        <SH>Adjustments</SH>
                        <div className="space-y-2.5">
                          {[
                            { icon:'fa-sun',        label:'Brightness', val:imgBrightness, set:setImgBrightness, min:-1, max:1, step:0.05 },
                            { icon:'fa-circle-half-stroke', label:'Contrast',   val:imgContrast,   set:setImgContrast,   min:-1, max:1, step:0.05 },
                            { icon:'fa-droplet',    label:'Saturation', val:imgSaturation, set:setImgSaturation, min:-1, max:1, step:0.05 },
                            { icon:'fa-wind',       label:'Blur',       val:imgBlur,       set:setImgBlur,       min: 0, max:1, step:0.05 },
                          ].map(a=>(
                            <div key={a.label}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[8.5px] font-semibold text-slate-500 flex items-center gap-1">
                                  <i className={`fas ${a.icon} text-[8px] text-slate-400`} />{a.label}
                                </span>
                                <span className="text-[8.5px] text-slate-400 font-mono w-8 text-right">
                                  {a.val>0?'+':''}{Math.round(a.val*100)}
                                </span>
                              </div>
                              <input
                                type="range" min={a.min} max={a.max} step={a.step} value={a.val}
                                onChange={e=>a.set(parseFloat(e.target.value))}
                                className="w-full accent-blue-500"
                              />
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={applyImageFilters}
                          className="w-full mt-3 py-2.5 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm"
                        >
                          <i className="fas fa-check text-[10px]" />Apply to Image
                        </button>
                        <button
                          onClick={()=>{ setImgBrightness(0); setImgContrast(0); setImgSaturation(0); setImgBlur(0); setImgFilter('none'); }}
                          className="w-full mt-1 py-1 text-[9px] text-slate-300 hover:text-slate-500 transition-colors text-center"
                        >
                          Reset all
                        </button>
                      </div>

                    </div>
                  )}

                  {/* Hint when uploads exist but nothing selected */}
                  {!isImgSelected && uploadedImages.length > 0 && (
                    <p className="text-[8.5px] text-slate-300 text-center pt-1">
                      <i className="fas fa-hand-pointer mr-1" />
                      Select an image on canvas to edit it
                    </p>
                  )}

                </div>
              );
            })()}

            {/* TEXT TAB */}
            {sideTab==='text' && (
              <div className="p-3 space-y-3">
                <SH>Add Text</SH>

                {/* AI Quick Add — generates and places on canvas in one click */}
                <button onClick={handleAiQuickAdd} disabled={isAiText}
                  className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-[11px] font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 shadow-sm transition-all">
                  <i className={`fas ${isAiText?'fa-spinner fa-spin':'fa-wand-magic-sparkles'} text-[10px]`} />
                  {isAiText ? 'Generating…' : 'AI Generate + Add'}
                </button>
                <p className="text-[9px] text-slate-400 -mt-1.5 text-center leading-tight">Generates headline from your post brief and places it on canvas</p>

                <div className="border-t border-slate-100 pt-2.5">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Or type manually</p>
                  <div className="relative">
                    <textarea value={textInput} onChange={(e)=>setTextInput(e.target.value)} placeholder="Type your text…" rows={2}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-[11px] text-slate-700 placeholder-slate-300 resize-none focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 pr-9" />
                    <button onClick={handleAiText} disabled={isAiText} title="AI prefill text"
                      className="absolute top-2 right-2 w-6 h-6 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-md flex items-center justify-center transition-all disabled:opacity-50">
                      <i className={`fas ${isAiText?'fa-spinner fa-spin':'fa-wand-magic-sparkles'} text-[9px]`} />
                    </button>
                  </div>
                  <p className="text-[9px] text-slate-400 mt-1 leading-tight">Wraps automatically · Drag corners to resize</p>
                  <button onClick={addText}
                    className="w-full mt-2 py-2 bg-slate-900 hover:bg-slate-700 text-white text-[11px] font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all">
                    <i className="fas fa-plus text-[9px]" />Add to Canvas
                  </button>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <SH>14 Text Styles</SH>
                  <p className="text-[9px] text-slate-400 -mt-1 mb-2 leading-tight">Select a style, then click AI Generate or Add to Canvas</p>
                  <div className="space-y-1">
                    {TEXT_STYLES.map(style=>(
                      <button key={style.id} onClick={()=>applyTextStyle(style)}
                        className={`w-full text-left px-2.5 py-2 rounded-xl border transition-all ${selectedStyleId===style.id?'border-blue-400 bg-blue-50 shadow-sm':'border-transparent hover:border-slate-200 hover:bg-slate-50'}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-bold text-slate-700">{style.name}</span>
                          <div className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0" style={{background:style.fill==='#ffffff'?'#94a3b8':style.fill}} />
                        </div>
                        <p className="text-[11px] truncate" style={{fontFamily:style.ff,fontWeight:style.fw,color:style.fill==='#ffffff'?'#64748b':style.fill}}>{style.sample}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* SHAPES TAB */}
            {sideTab==='shapes' && (
              <div className="p-3 space-y-3">
                <SH>Shapes</SH>
                <div className="grid grid-cols-4 gap-1.5">
                  {SHAPES.map(s=>(
                    <button key={s.id} onClick={()=>addShape(s.id)} title={s.label}
                      className="flex flex-col items-center gap-1 p-2 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all">
                      <i className={`${s.icon} text-slate-600 text-sm`} />
                      <span className="text-[8px] text-slate-500">{s.label}</span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <SH>Shape Style</SH>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-slate-500 w-10">Fill</span>
                    <input type="color" value={shapeFill} onChange={e=>setShapeFill(e.target.value)} className="w-8 h-7 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                    <span className="text-[9px] text-slate-400 truncate">{shapeFill}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-slate-500 w-10">Stroke</span>
                    <input type="color" value={shapeStroke||'#000000'} onChange={e=>setShapeStroke(e.target.value)} className="w-8 h-7 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                    <input type="number" min="0" max="20" value={strokeWidth} onChange={e=>setStrokeWidth(parseInt(e.target.value)||0)}
                      className="w-10 h-7 px-1 text-[10px] border border-slate-200 rounded-lg focus:outline-none" placeholder="px" />
                  </div>
                  <button onClick={()=>{ const cv=fabricRef.current; if(!cv) return; const o=cv.getActiveObject(); if(!o) return; o.set({fill:shapeFill,stroke:shapeStroke||undefined,strokeWidth}); cv.renderAll(); saveToHistory(); }}
                    disabled={!hasObj}
                    className="w-full py-1.5 text-[10px] font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-40">
                    Apply Style to Selected
                  </button>
                </div>
              </div>
            )}

            {/* BRAND TAB */}
            {sideTab==='brand' && (
              <div className="p-3 space-y-4">
                <SH>Brand Colours</SH>
                <div className="flex gap-2">
                  {[{label:'Primary',color:brandColors.primary},{label:'Secondary',color:brandColors.secondary},{label:'Accent',color:brandColors.accent}].map(c=>(
                    <button key={c.label} onClick={()=>setProp('fill',c.color)} title={`Apply ${c.label}`} className="flex flex-col items-center gap-1 flex-1 group">
                      <div className="w-full h-8 rounded-xl border border-slate-200 group-hover:ring-2 group-hover:ring-blue-300 transition-all shadow-sm" style={{background:c.color}} />
                      <span className="text-[8px] font-medium text-slate-400">{c.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[9px] text-slate-300 text-center -mt-2">Tap swatch → applies colour to selected</p>
                <div className="border-t border-slate-100 pt-3">
                  <SH>Logo</SH>
                  {logoDataUrl ? (
                    <div className="space-y-2">
                      <div className="rounded-xl border border-slate-200 p-2 flex items-center justify-center bg-slate-50"
                        style={{backgroundImage:'repeating-conic-gradient(#e2e8f0 0% 25%, white 0% 50%)',backgroundSize:'12px 12px',minHeight:60}}>
                        <img src={logoDataUrl} alt="Logo" className="max-h-12 max-w-full object-contain" />
                      </div>
                      <button
                        onClick={handleRemoveLogoBg}
                        disabled={isRemovingLogoBg}
                        className="w-full py-2 text-[10px] font-bold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 rounded-xl flex flex-col items-center justify-center gap-1 transition-all shadow-sm">
                        {isRemovingLogoBg ? (
                          <>
                            <div className="flex items-center gap-1.5">
                              <i className="fas fa-circle-notch fa-spin text-[9px]" />
                              <span>{bgRemoveProgress.label || 'Processing…'}</span>
                            </div>
                            {bgRemoveProgress.pct > 0 && (
                              <div className="w-4/5 bg-white/25 rounded-full h-1 mt-0.5">
                                <div className="bg-white rounded-full h-1 transition-all duration-300" style={{ width: `${bgRemoveProgress.pct}%` }} />
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <i className="fas fa-wand-magic-sparkles text-[9px]" />
                            Remove Background (AI)
                          </div>
                        )}
                      </button>
                      <p className="text-[8px] text-slate-400 text-center flex items-center justify-center gap-1 -mt-1">
                        <i className="fas fa-microchip text-violet-400 text-[7px]" />BRIAAI RMBG-1.4 · free
                      </p>
                      <button onClick={addLogoToCanvas}
                        className="w-full py-1.5 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all flex items-center justify-center gap-1.5">
                        <i className="fas fa-plus text-[9px]" />Add to Canvas
                      </button>
                      <button onClick={()=>setLogoDataUrl('')} className="w-full py-0.5 text-[9px] text-slate-300 hover:text-red-400 text-center">Remove logo</button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group">
                      <i className="fas fa-cloud-upload-alt text-slate-300 group-hover:text-blue-400 text-xl mb-2 transition-all" />
                      <span className="text-[10px] text-slate-400 group-hover:text-blue-600 font-medium">Upload logo (PNG/SVG)</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    </label>
                  )}
                </div>
                <div className="border-t border-slate-100 pt-3">
                  <SH>Website / CTA</SH>
                  <div className="flex gap-1.5">
                    <input type="text" id="ds-url-input" placeholder="https://yoursite.com"
                      className="flex-1 min-w-0 border border-slate-200 rounded-xl px-2 py-1.5 text-[10px] text-slate-700 placeholder-slate-300 focus:outline-none focus:border-blue-400 transition-all" />
                    <button onClick={addUrlToCanvas}
                      className="px-2.5 py-1.5 bg-slate-900 hover:bg-slate-700 text-white text-[10px] font-bold rounded-xl transition-all whitespace-nowrap">Add</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>{/* end sidebar */}

        {/* CANVAS AREA */}
        <div className="flex-1 flex items-center justify-center overflow-auto pt-8 px-8 pb-14 relative"
          style={{background:'radial-gradient(circle, #94a3b8 1px, transparent 1px)', backgroundSize:'24px 24px', backgroundColor:'#e2e8f0'}}>

          {/* Canvas shadow + frame */}
          <div className="relative flex-shrink-0" style={{transform:`scale(${zoom})`, transformOrigin:'center center'}}>
            <div className="absolute -inset-2 bg-white/20 rounded-xl blur-sm" />
            <div className="relative bg-white shadow-2xl overflow-hidden"
              style={{width:CANVAS_W, height:CANVAS_H, boxShadow:'0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)'}}>
              <canvas ref={canvasElRef} style={{display:'block'}} />
              {!canvasReady && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50">
                  <i className="fas fa-spinner fa-spin text-blue-500 text-3xl mb-3" />
                  <span className="text-sm font-medium text-slate-500">Initialising canvas…</span>
                  <span className="text-xs text-slate-400 mt-1">Loading fabric.js</span>
                </div>
              )}
            </div>
            {/* Canvas size label */}
            <div className="absolute -bottom-6 left-0 right-0 flex justify-center">
              <span className="text-[9px] text-slate-400 font-medium">{CANVAS_W} × {CANVAS_H}px · {ASPECT_RATIOS[selectedAspect].label}</span>
            </div>
          </div>

          {/* ── Unified bottom bar: autosave (left) · hints (centre) · zoom (right) ── */}
          <div className="absolute bottom-0 left-0 right-0 h-11 bg-white/96 backdrop-blur-md border-t border-slate-200 flex items-center px-4 gap-2 shadow-sm">

            {/* Left — autosave status pill */}
            <div className="flex-shrink-0 w-36">
              {autoSaveStatus === 'saving' && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full animate-pulse">
                  <i className="fas fa-circle-notch fa-spin text-blue-500 text-[9px]" />
                  <span className="text-[9px] text-blue-600 font-semibold whitespace-nowrap">Saving…</span>
                </div>
              )}
              {autoSaveStatus === 'saved' && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                  <i className="fas fa-check-circle text-emerald-500 text-[9px]" />
                  <span className="text-[9px] text-emerald-600 font-semibold whitespace-nowrap">Auto-saved ✓</span>
                </div>
              )}
              {autoSaveStatus === 'idle' && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full">
                  <i className="fas fa-cloud text-slate-300 text-[9px]" />
                  <span className="text-[9px] text-slate-300 font-medium whitespace-nowrap">Auto-save on</span>
                </div>
              )}
            </div>

            {/* Centre — keyboard hints */}
            <div className="flex-1 flex items-center justify-center gap-3 pointer-events-none select-none">
              <span className="text-[9px] text-slate-400 whitespace-nowrap">
                <i className="fas fa-i-cursor mr-1 text-purple-400" />Double-click to edit
              </span>
              <span className="text-slate-200">|</span>
              <span className="text-[9px] text-slate-400 whitespace-nowrap">
                <i className="fas fa-expand-arrows-alt mr-1 text-blue-400" />Drag corner · wrap text
              </span>
              <span className="text-slate-200">|</span>
              <span className="text-[9px] text-slate-400 whitespace-nowrap">
                <kbd className="bg-slate-100 border border-slate-200 px-1 rounded text-[8px] font-mono">Ctrl+Z</kbd> Undo
              </span>
              <span className="text-slate-200">|</span>
              <span className="text-[9px] text-slate-400 whitespace-nowrap">
                <kbd className="bg-slate-100 border border-slate-200 px-1 rounded text-[8px] font-mono">Del</kbd> Delete
              </span>
            </div>

            {/* Right — zoom controls */}
            <div className="flex items-center gap-1 w-32 justify-end flex-shrink-0">
              <button onClick={()=>setZoom(z=>Math.max(0.25,+(z-0.1).toFixed(1)))}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-all">
                <i className="fas fa-minus text-[10px]" />
              </button>
              <span className="text-[11px] text-slate-600 w-10 text-center font-semibold">{Math.round(zoom*100)}%</span>
              <button onClick={()=>setZoom(z=>Math.min(2,+(z+0.1).toFixed(1)))}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-all">
                <i className="fas fa-plus text-[10px]" />
              </button>
              <div className="w-px h-4 bg-slate-200 mx-0.5" />
              <button onClick={()=>setZoom(1)}
                className="px-2 h-6 rounded-full hover:bg-slate-100 text-slate-500 text-[9px] font-bold transition-all">
                FIT
              </button>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
